/**
 * AssembliesPage — AI draft browsing and management.
 *
 * Home for browsing AI drafts and launching the Create AI Draft page.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Layers, Scissors, Check, X, Trash2, Edit2, ChevronDown, Sparkles, Loader2, AlertCircle, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import apiClient from '../services/api'
import GlobalSearch from '../components/GlobalSearch'
import ViewToggle from '../components/ViewToggle'
import { isDegraded, isGenerationInProgress } from '../utils/generationStatus'
import { apiErrorDetail } from '../utils/dataValidator'

interface Project {
  id: string
  name: string
  asset_count: number
}

interface AssemblyListItem {
  id: number
  project_id: string
  name: string
  description: string | null
  status: string
  assembly_type: string
  draft_origin?: 'created' | 'suggested' | string
  generation_status: string
  sections_count: number
  total_moments: number
  created_time: string | null
  updated_time: string | null
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  const d = new Date(dateStr)
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

export default function AssembliesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
    searchParams.get('projects') ? searchParams.get('projects')!.split(',') : []
  )
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [showProjectPicker, setShowProjectPicker] = useState(false)

  const [assemblies, setAssemblies] = useState<AssemblyListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  const didAutoSelect = useRef(false)

  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true)
      const res = await apiClient.get('/projects/')
      setProjects(res.data.projects)
      if (!didAutoSelect.current && res.data.projects.length > 0) {
        didAutoSelect.current = true
        const fromUrl = searchParams.get('projects')
        if (!fromUrl) {
          const allIds = res.data.projects.map((p: Project) => p.id)
          setSelectedProjectIds(allIds)
          setSearchParams({ projects: allIds.join(',') })
        }
      }
    } catch {
      // non-critical
    } finally {
      setProjectsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSearchParams])

  useEffect(() => { fetchProjects() }, [fetchProjects])

  const fetchData = useCallback(async () => {
    if (selectedProjectIds.length === 0) return
    try {
      setLoading(true)
      setError(null)
      const projectsParam = selectedProjectIds.join(',')
      const res = await apiClient.get('/assemblies/', { params: { project_ids: projectsParam } })
      setAssemblies((res.data.assemblies || []).filter((a: AssemblyListItem) => a.assembly_type === 'ai_draft'))
    } catch (err) {
      setError(apiErrorDetail(err, 'Failed to load assemblies'))
    } finally {
      setLoading(false)
    }
  }, [selectedProjectIds])

  useEffect(() => { fetchData() }, [fetchData])

  const toggleProject = (id: string) => {
    const next = selectedProjectIds.includes(id)
      ? selectedProjectIds.filter(p => p !== id)
      : [...selectedProjectIds, id]
    setSelectedProjectIds(next)
    setSearchParams(next.length > 0 ? { projects: next.join(',') } : {})
  }

  const toggleAllProjects = () => {
    const allSelected = selectedProjectIds.length === projects.length
    const next = allSelected ? [] : projects.map(p => p.id)
    setSelectedProjectIds(next)
    setSearchParams(next.length > 0 ? { projects: next.join(',') } : {})
  }

  const selectorText = projectsLoading
    ? 'Loading...'
    : selectedProjectIds.length === 0
      ? 'Select workspaces'
      : selectedProjectIds.length === projects.length && projects.length > 1
        ? 'All workspaces'
        : selectedProjectIds.length === 1
          ? projects.find(p => p.id === selectedProjectIds[0])?.name || 'Select workspaces'
          : `${selectedProjectIds.length} workspaces selected`

  const deleteAssembly = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!confirm('Delete this assembly? This cannot be undone.')) return
    try {
      await apiClient.delete(`/assemblies/${id}`)
      toast.success('Assembly deleted')
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to delete assembly'))
    }
  }

  const saveEdit = async (id: number) => {
    if (!editName.trim()) return
    try {
      await apiClient.patch(`/assemblies/${id}`, { name: editName.trim() })
      setEditingId(null)
      fetchData()
    } catch (err) {
      toast.error(apiErrorDetail(err, 'Failed to update assembly'))
    }
  }

  const typeBadge = () => (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-200">
      <Sparkles className="w-3 h-3" />
      AI Draft
    </span>
  )

  const originBadge = (origin?: string) => {
    if (origin === 'suggested') {
      return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-200">
          Suggested
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
        Created
      </span>
    )
  }

  const statusBadge = (status: string) => {
    if (status === 'review') {
      return (
        <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-200">
          Review
        </span>
      )
    }
    if (status === 'final') {
      return (
        <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
          Final
        </span>
      )
    }
    return (
      <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-[var(--rq-text-soft)]">
        Draft
      </span>
    )
  }

  const generationBadge = (status: string) => {
    if (isGenerationInProgress(status)) {
      return (
        <span className="rq-status-chip rq-status-chip-generating inline-flex items-center gap-1 text-[10px] px-2 py-0.5 flex-shrink-0">
          <Loader2 className="w-3 h-3 animate-spin" />
          Generating
        </span>
      )
    }
    if (status === 'failed') {
      return (
        <span className="rq-status-chip rq-status-chip-failed inline-flex items-center gap-1 text-[10px] px-2 py-0.5 flex-shrink-0">
          <AlertCircle className="w-3 h-3" />
          Failed
        </span>
      )
    }
    // A degraded draft is finished and openable, so this is a caveat rather
    // than an error. Styled with utilities rather than a new rq-status-chip
    // variant, which would need theme variables in both colour schemes.
    if (isDegraded(status)) {
      return (
        <span className="rq-status-chip inline-flex items-center gap-1 text-[10px] px-2 py-0.5 flex-shrink-0 border-amber-500/30 bg-amber-500/10 text-amber-300">
          <AlertTriangle className="w-3 h-3" />
          Reduced
        </span>
      )
    }
    return null
  }

  return (
    <div className="rq-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="rq-title">Assemblies</h1>
          <p className="rq-subtitle">Browse, modify, and export AI drafts.</p>
        </div>
        <button
          onClick={() => navigate('/create-ai-draft')}
          className="rq-btn-primary px-3 py-2 text-sm"
        >
          <Sparkles className="w-4 h-4" />
          Create AI Draft
        </button>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <GlobalSearch inline />
      </div>

      <div className="relative mb-8">
        <button
          onClick={() => setShowProjectPicker(!showProjectPicker)}
          className="flex items-center gap-2 px-4 py-2.5 rq-card rq-card-hover min-w-[200px]"
        >
          <Layers className="w-4 h-4 text-[var(--rq-text-soft)]" />
          <span className="flex-1 text-left truncate text-[var(--rq-text)]">{selectorText}</span>
          <ChevronDown className={`w-4 h-4 text-[var(--rq-text-soft)] transition-transform ${showProjectPicker ? 'rotate-180' : ''}`} />
        </button>

        {showProjectPicker && (
          <div className="absolute z-20 mt-1 w-full max-w-sm rq-card shadow-xl overflow-hidden">
            {projects.length > 1 && (
              <button
                onClick={toggleAllProjects}
                className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3 border-b border-white/10"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  selectedProjectIds.length === projects.length
                    ? 'bg-[var(--btn)] border-[var(--btn)]'
                    : 'border-white/30'
                }`}>
                  {selectedProjectIds.length === projects.length && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-[var(--rq-text)] text-sm font-medium">All Workspaces</span>
              </button>
            )}
            {projects.map(p => (
              <button
                key={p.id}
                onClick={() => toggleProject(p.id)}
                className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3"
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                  selectedProjectIds.includes(p.id)
                    ? 'bg-[var(--btn)] border-[var(--btn)]'
                    : 'border-white/30'
                }`}>
                  {selectedProjectIds.includes(p.id) && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-[var(--rq-text)] text-sm font-medium truncate flex-1">{p.name}</span>
                <span className="text-xs text-[var(--rq-text-soft)] ml-2">{p.asset_count} asset{p.asset_count !== 1 ? 's' : ''}</span>
              </button>
            ))}
            {projects.length === 0 && (
              <div className="px-4 py-3 text-sm text-[var(--rq-text-soft)]">No workspaces yet</div>
            )}
          </div>
        )}
      </div>

      {selectedProjectIds.length === 0 ? (
        <div className="text-center py-20">
          <Scissors className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">Select at least one workspace</h2>
          <p className="text-[var(--rq-text-soft)] mb-5">Choose workspace scope first, then create or manage AI drafts.</p>
          <button
            onClick={() => setShowProjectPicker(true)}
            className="rq-btn-primary px-4 py-2 text-sm"
          >
            <Layers className="w-4 h-4" />
            Choose Workspaces
          </button>
        </div>
      ) : loading ? (
        <div className="text-center py-20 text-[var(--rq-text-soft)]">Loading assemblies...</div>
      ) : error ? (
        <div className="text-center py-20">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="rq-btn-ghost px-4 py-2 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[var(--rq-text-soft)]">
              {assemblies.length} assembl{assemblies.length !== 1 ? 'ies' : 'y'}
            </p>
            <ViewToggle mode={viewMode} onChange={setViewMode} />
          </div>

          {assemblies.length === 0 && (
            <div className="text-center py-20">
              <Scissors className="w-16 h-16 text-[var(--rq-text-soft)] mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-[var(--rq-text)] mb-2">No AI drafts yet</h2>
              <p className="text-[var(--rq-text-soft)] max-w-md mx-auto mb-5">
                Start by creating your first AI Draft, then modify and export it.
              </p>
              <button onClick={() => navigate('/create-ai-draft')} className="rq-btn-primary px-4 py-2 text-sm">
                <Sparkles className="w-4 h-4" />
                Create AI Draft
              </button>
            </div>
          )}

          {assemblies.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {assemblies.map(assembly => (
                <div
                  key={assembly.id}
                  onClick={() => navigate(`/assemblies/${assembly.id}`)}
                  className="rq-card rq-card-hover p-5 cursor-pointer space-y-3"
                >
                  {editingId === assembly.id ? (
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <input
                      autoFocus
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="rq-input-dark text-sm flex-1"
                      onKeyDown={e => e.key === 'Enter' && saveEdit(assembly.id)}
                    />
                    <button onClick={() => saveEdit(assembly.id)} className="text-green-400 hover:text-green-300 p-1">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-[var(--rq-text)] font-medium truncate">{assembly.name}</p>
                        {generationBadge(assembly.generation_status)}
                      </div>
                      {assembly.description && (
                        <p className="text-xs text-[var(--rq-text-soft)] truncate mt-0.5">{assembly.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingId(assembly.id); setEditName(assembly.name) }}
                        className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                        title="Rename"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => deleteAssembly(e, assembly.id)}
                        className="text-[var(--rq-text-soft)] hover:text-red-400 p-1 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                  <div className="flex flex-wrap items-center gap-2">
                    {typeBadge()}
                    {originBadge(assembly.draft_origin)}
                    {statusBadge(assembly.status)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-[var(--rq-text-soft)]">Sections: <span className="text-[var(--rq-text)]">{assembly.sections_count}</span></div>
                    <div className="text-[var(--rq-text-soft)]">Clips: <span className="text-[var(--rq-text)]">{assembly.total_moments}</span></div>
                    <div className="text-[var(--rq-text-soft)] col-span-2">Updated: <span className="text-[var(--rq-text)]">{formatDate(assembly.updated_time || assembly.created_time)}</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {assemblies.length > 0 && viewMode === 'list' && (
            <div className="space-y-3">
              <div className="hidden lg:grid grid-cols-[1fr_100px_100px_70px_80px_80px_140px_100px] items-center gap-4 px-4 py-3 border-b border-white/10 text-sm font-medium text-[var(--rq-text-soft)]">
                <span>Name</span>
                <span className="text-center">Type</span>
                <span className="text-center">Origin</span>
                <span className="text-center">Status</span>
                <span className="text-center">Sections</span>
                <span className="text-center">Clips</span>
                <span className="text-right">Updated</span>
                <span className="text-right">Actions</span>
              </div>

              {assemblies.map(assembly => (
                <div key={assembly.id}>
                  <div
                    onClick={() => navigate(`/assemblies/${assembly.id}`)}
                    className="hidden lg:grid grid-cols-[1fr_100px_100px_70px_80px_80px_140px_100px] items-center gap-4 px-4 py-3 rq-card rq-card-hover cursor-pointer"
                  >
                    {editingId === assembly.id ? (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <input
                          autoFocus
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="rq-input-dark text-sm flex-1"
                          onKeyDown={e => e.key === 'Enter' && saveEdit(assembly.id)}
                        />
                        <button onClick={() => saveEdit(assembly.id)} className="text-green-400 hover:text-green-300 p-1">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-[var(--rq-text)] font-medium truncate">{assembly.name}</p>
                          {generationBadge(assembly.generation_status)}
                        </div>
                        {assembly.description && (
                          <p className="text-xs text-[var(--rq-text-soft)] truncate mt-0.5">{assembly.description}</p>
                        )}
                      </div>
                    )}
                    <div className="flex justify-center">{typeBadge()}</div>
                    <div className="flex justify-center">{originBadge(assembly.draft_origin)}</div>
                    <div className="flex justify-center">{statusBadge(assembly.status)}</div>
                    <span className="text-sm text-[var(--rq-text-soft)] text-center">{assembly.sections_count}</span>
                    <span className="text-sm text-[var(--rq-text-soft)] text-center">{assembly.total_moments}</span>
                    <span className="text-sm text-[var(--rq-text-soft)] text-right tabular-nums">{formatDate(assembly.updated_time || assembly.created_time)}</span>
                    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingId(assembly.id); setEditName(assembly.name) }}
                        className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                        title="Rename"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => deleteAssembly(e, assembly.id)}
                        className="text-[var(--rq-text-soft)] hover:text-red-400 p-1 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div
                    onClick={() => navigate(`/assemblies/${assembly.id}`)}
                    className="lg:hidden rq-card rq-card-hover p-5 cursor-pointer space-y-3"
                  >
                    {editingId === assembly.id ? (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <input
                          autoFocus
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="rq-input-dark text-sm flex-1"
                          onKeyDown={e => e.key === 'Enter' && saveEdit(assembly.id)}
                        />
                        <button onClick={() => saveEdit(assembly.id)} className="text-green-400 hover:text-green-300 p-1">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-[var(--rq-text)] font-medium truncate">{assembly.name}</p>
                            {generationBadge(assembly.generation_status)}
                          </div>
                          {assembly.description && (
                            <p className="text-xs text-[var(--rq-text-soft)] truncate mt-0.5">{assembly.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingId(assembly.id); setEditName(assembly.name) }}
                            className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] p-1 transition-colors"
                            title="Rename"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => deleteAssembly(e, assembly.id)}
                            className="text-[var(--rq-text-soft)] hover:text-red-400 p-1 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      {typeBadge()}
                      {originBadge(assembly.draft_origin)}
                      {statusBadge(assembly.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="text-[var(--rq-text-soft)]">Sections: <span className="text-[var(--rq-text)]">{assembly.sections_count}</span></div>
                      <div className="text-[var(--rq-text-soft)]">Clips: <span className="text-[var(--rq-text)]">{assembly.total_moments}</span></div>
                      <div className="text-[var(--rq-text-soft)]">Updated: <span className="text-[var(--rq-text)]">{formatDate(assembly.updated_time || assembly.created_time)}</span></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
